import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import RichNoteEditor from '../../components/Notes/RichNoteEditor';
import { useNotes } from '../../contexts/NotesContext';
import { useAuth } from '../../contexts/AuthContext';
import { useConnectionMonitor } from '../../contexts/ConnectionMonitorContext';
import { socketManager } from '../../services/SocketManager';
import { Loader2, Crown, Lock, WifiOff } from 'lucide-react';

// Legacy key: notesplus_pending_queue_{userId}
// Product policy (82/83): do not write, flush, hydrate, or delete these entries.
// New offline writes use notesplus_offline_notes_{userId} (see utils/offlineNoteQueue.js).

const NotesPage = () => {
  const { t } = useTranslation('notes');
  const { selectedNote, selectNote, updateNote, isLoading } = useNotes();
  const { isPlusUser, user } = useAuth();
  const { isServerOnline } = useConnectionMonitor();
  // Ortak çalışma notu (sunucudan gelen canonical alanlar) — çevrimdışıyken salt okunur
  const isSharedActiveNote = !!(selectedNote?.isCollaborative || selectedNote?.myRole === 'editor');
  const isOfflineSharedReadOnly = !isServerOnline && isSharedActiveNote;
  const isCollaborativeReadOnly = selectedNote?.isCollaborative && !isPlusUser;
  // Mevcut kullanıcı bu ortak notta dondurulmuşsa salt okunur göster
  const isFrozenCollaborator = !!(
    selectedNote?.myRole === 'editor' &&
    selectedNote?.collaborators?.find(
      (c) => String(c.user_id) === String(user?.id) && c.is_frozen
    )
  );
  const [title, setTitle] = useState(() => selectedNote?.title || '');
  const [content, setContent] = useState(() => selectedNote?.content || '');
  const [draft, setDraft] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved');

  // Bağlantı durumu geçişini (false→true) tespit etmek için
  const prevOnlineRef = useRef(isServerOnline);
  const isServerOnlineRef = useRef(isServerOnline);
  useEffect(() => {
    isServerOnlineRef.current = isServerOnline;
  }, [isServerOnline]);
  // user.id'yi her zaman güncel tutan ref (closure sorununu önler)
  const userIdRef = useRef(user?.id);
  useEffect(() => { userIdRef.current = user?.id; }, [user?.id]);
  // selectedNote'u her zaman güncel tutan ref — autosave closure'da stale olmaz
  const selectedNoteRef = useRef(selectedNote);
  useEffect(() => { selectedNoteRef.current = selectedNote; }, [selectedNote]);
  // Son başarıyla kaydedilen içerik — socket güncellemeleriyle karışmadan hasChanges hesabı için
  const lastSavedRef = useRef(
    selectedNote
      ? { title: selectedNote.title || '', content: selectedNote.content || '', noteId: selectedNote.id }
      : { title: '', content: '', noteId: null }
  );
  // RichNoteEditor'ın applyDelta metoduna erişim için ref
  const collaborationRef = useRef(null);
  // Şu an düzenlenen noteId — socket odası yönetimi için
  const activeNoteRoomRef = useRef(null);
  // Overwrite tespiti: her zaman güncel title/content ref'leri
  const contentRef = useRef(content);
  useEffect(() => { contentRef.current = content; }, [content]);
  const titleRef = useRef(title);
  useEffect(() => { titleRef.current = title; }, [title]);

  const resolveSaveTitle = useCallback(() => {
    const fromRef = titleRef.current?.trim();
    if (fromRef) return fromRef;
    const fromContext = selectedNoteRef.current?.title?.trim();
    if (fromContext) return fromContext;
    return t('common:unnamed');
  }, [t]);
  // Aynı not için birden fazla re-save döngüsü önlemi — kullanıcı yeni bir şey yazarsa sıfırlanır
  const retriedOverwriteRef = useRef(null);
  // Overwrite tespiti tetikleyici (auto-save useEffect'i zorla çalıştırır)
  const [overwriteCounter, setOverwriteCounter] = useState(0);
  // Hangi notun title/content state'ine yüklendiğini takip et (render sırasında senkron reset)
  // null ile başlat — selectedNote.id ile başlatılırsa sekme dönüşünde hydrate atlanır
  const [loadedNoteId, setLoadedNoteId] = useState(null);

  // Not değişince title/content'i paint öncesi senkron güncelle — çocuk editör eski içerik görmez
  const activeNoteId = selectedNote?.id ?? null;
  if (activeNoteId !== loadedNoteId) {
    if (!selectedNote) {
      setLoadedNoteId(null);
      setTitle('');
      setContent('');
      setSaveStatus('saved');
      setDraft(null);
      lastSavedRef.current = { title: '', content: '', noteId: null };
    } else {
      // Legacy pending queue is preserved in localStorage but never auto-applied
      // (would overwrite collab notes / imply offline edits are current).
      setLoadedNoteId(activeNoteId);
      setTitle(selectedNote.title || '');
      setContent(selectedNote.content || '');
      setSaveStatus('saved');
      lastSavedRef.current = {
        title: selectedNote.title || '',
        content: selectedNote.content || '',
        noteId: activeNoteId,
      };
    }
  }

  // ─── Not düzenleme socket odası yönetimi + delta senkronizasyonu ────────────
  useEffect(() => {
    const noteId = selectedNote?.id;
    const socket = socketManager.getSocket();

    // Önceki notu bırak
    if (activeNoteRoomRef.current && activeNoteRoomRef.current !== String(noteId)) {
      socket?.emit('note:leave-editing', { noteId: activeNoteRoomRef.current });
      activeNoteRoomRef.current = null;
    }

    // Yeni nota katıl (ortak not veya kendi notu, sadece yazılabilir durumda)
    // isServerOnline dependency: bağlantı geri gelince reload beklemeden odaya yeniden girilir
    if (noteId && socket && isServerOnline && !isCollaborativeReadOnly && !isFrozenCollaborator) {
      socket.emit('note:join-editing', { noteId });
      activeNoteRoomRef.current = String(noteId);
    }

    return () => {
      // Sayfa unmount veya not değişince odayı bırak
      if (activeNoteRoomRef.current) {
        socketManager.getSocket()?.emit('note:leave-editing', { noteId: activeNoteRoomRef.current });
        activeNoteRoomRef.current = null;
      }
    };
  }, [selectedNote?.id, isCollaborativeReadOnly, isFrozenCollaborator, isServerOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  // Uzaktan gelen delta'ları editöre uygula
  useEffect(() => {
    const socket = socketManager.getSocket();
    if (!socket) return;

    const handleRemoteDelta = ({ noteId, delta }) => {
      // Sadece şu an açık nota ait delta'ları uygula
      if (String(noteId) !== String(selectedNote?.id)) return;
      collaborationRef.current?.applyDelta(delta);
    };

    socket.on('note:delta', handleRemoteDelta);
    return () => socket.off('note:delta', handleRemoteDelta);
  }, [selectedNote?.id, isServerOnline]);

  // Header / sidebar başlık düzenlemesini NotesPage state'ine anında yansıt (autosave çakışması önlemi)
  useEffect(() => {
    const handleTitleSync = (event) => {
      const { noteId, title: newTitle } = event.detail || {};
      if (!noteId || String(noteId) !== String(selectedNoteRef.current?.id)) return;
      const normalized = (newTitle || '').trim() || t('common:unnamed');
      titleRef.current = normalized;
      setTitle(normalized);
      if (lastSavedRef.current.noteId === noteId) {
        lastSavedRef.current = { ...lastSavedRef.current, title: normalized };
      }
    };
    window.addEventListener('notesplus-note-title-sync', handleTitleSync);
    return () => window.removeEventListener('notesplus-note-title-sync', handleTitleSync);
  }, []);

  // ─── Not değişince önceki notu kaydet (geçişte içerik kaybı önlemi) ───────────
  useLayoutEffect(() => {
    const mountedId = selectedNote?.id ?? null;
    const mountedNote = selectedNote;
    if (!mountedId || !mountedNote) return undefined;

    return () => {
      // Çevrimdışı ortak not: kaydetme yok (kuyruğa da girmez). Kişisel not kuyruğa yazılır.
      if (!isServerOnlineRef.current && (mountedNote.isCollaborative || mountedNote.myRole === 'editor')) return;
      const saved = lastSavedRef.current;
      if (saved.noteId !== mountedId) return;
      const nextContent = contentRef.current;
      const nextTitle = resolveSaveTitle();
      if (nextTitle === saved.title && nextContent === saved.content) return;

      // Autosave ile çift istek önlemi — flush başarısız olursa autosave tekrar dener
      lastSavedRef.current = { title: nextTitle, content: nextContent, noteId: mountedId };

      updateNote(
        { ...mountedNote, title: nextTitle, content: nextContent },
        { showToast: false, skipRollback: true }
      ).catch(() => {
        lastSavedRef.current = saved;
      });
    };
  }, [selectedNote?.id, updateNote]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Context'ten başlık değişince NotesPage state'ini senkronize et ─────────
  useEffect(() => {
    if (!selectedNote?.id) return;
    // Legacy pending entries are not applied — always sync from server/context title.

    const contextTitle = selectedNote.title || '';
    const localTitle = titleRef.current?.trim();

    // Autosave'in yanlışlıkla yazdığı "Başlıksız Not" yerel gerçek başlığı ezmesin
    if (localTitle && localTitle !== contextTitle) {
      const isContextPlaceholder = !contextTitle.trim() || contextTitle === t('common:unnamed');
      if (isContextPlaceholder && localTitle !== t('common:unnamed')) return;
    }

    setTitle(contextTitle);
    if (lastSavedRef.current.noteId === selectedNote.id) {
      lastSavedRef.current = { ...lastSavedRef.current, title: contextTitle };
    }
  }, [selectedNote?.title, selectedNote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Taslak kontrolü ─────────────────────────────────────────────────────────
  // Not açıldığında localStorage'da taslak var mı kontrol et (Faz 1)
  useEffect(() => {
    if (!selectedNote?.id || !user?.id) { setDraft(null); return; }
    try {
      const stored = window.localStorage.getItem(`notesplus_draft_${user.id}_${selectedNote.id}`);
      setDraft(stored ? JSON.parse(stored) : null);
    } catch { setDraft(null); }
  }, [selectedNote?.id, user?.id]);

  // NotesContext taslak kaydedince anında banneri göster (Faz 1)
  useEffect(() => {
    const handleDraftSaved = (event) => {
      if (event.detail?.noteId !== selectedNote?.id || !user?.id) return;
      try {
        const stored = window.localStorage.getItem(`notesplus_draft_${user.id}_${event.detail.noteId}`);
        if (stored) setDraft(JSON.parse(stored));
      } catch { /* ignore */ }
    };
    window.addEventListener('notesplus-draft-saved', handleDraftSaved);
    return () => window.removeEventListener('notesplus-draft-saved', handleDraftSaved);
  }, [selectedNote?.id, user?.id]);

  // ─── Auto-save (debounced) ────────────────────────────────────────────────────
  // ÖNEMLI: Dependency array'inde selectedNote YOK — socket güncellemeleri (diğer editörlerin
  // kaydetmeleri) timer'ı sıfırlamasın. Mevcut not context'i selectedNoteRef üzerinden okunur.
  useEffect(() => {
    const activeNoteId = selectedNoteRef.current?.id;
    const saved = lastSavedRef.current;
    if (!activeNoteId || saved.noteId !== activeNoteId) return;

    const hasChanges = title !== saved.title || content !== saved.content;
    if (!hasChanges) return;

    const currentNoteId = activeNoteId;

    const timer = setTimeout(async () => {
      // Not değiştiyse (başka nota geçildiyse) bu save'i iptal et
      if (lastSavedRef.current.noteId !== currentNoteId) return;

      const noteForSave = selectedNoteRef.current;
      if (!noteForSave || noteForSave.id !== currentNoteId) return;

      const saveTitle = resolveSaveTitle();
      const saveContent = contentRef.current;
      const sharedNow = !!(noteForSave.isCollaborative || noteForSave.myRole === 'editor');

      // Çevrimdışı ortak not: kayıt yok, kuyruk yok
      if (!isServerOnline && sharedNow) {
        setSaveStatus('offline');
        return;
      }

      // Çevrimdışı kişisel not: updateNote yerel state'i günceller + offline kuyruğa yazar
      if (!isServerOnline) {
        try {
          await updateNote(
            { ...noteForSave, title: saveTitle, content: saveContent },
            { showToast: false }
          );
          lastSavedRef.current = { title: saveTitle, content: saveContent, noteId: currentNoteId };
          setSaveStatus('offline');
        } catch {
          setSaveStatus('draft');
        }
        return;
      }

      setSaveStatus('saving');
      try {
        await updateNote({
          ...noteForSave,
          title: saveTitle,
          content: saveContent,
        });
        // Başarılı kayıt sonrası ref'i güncelle
        lastSavedRef.current = { title: saveTitle, content: saveContent, noteId: currentNoteId };
        // Do not mutate legacy notesplus_pending_queue_* — preserved for future recovery UI.
        setSaveStatus('saved');
        setDraft(null);
      } catch {
        setSaveStatus('draft');
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [title, content, selectedNote?.id, updateNote, isServerOnline, overwriteCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Ortak not: başka kullanıcı üzerimize yazdıysa kendi içeriğimizi yeniden kaydet ──────
  // Senaryo: A "TEB" kaydeder → B (eski içerikle) "TEB9" kaydeder → DB = "TEB9"
  // A'nın lastSavedRef = "TEB", content = "TEB" → hasChanges = false → re-save olmaz
  // Bu listener: overwrite tespit edip lastSavedRef'i sıfırlayarak bir kez re-save tetikler.
  useEffect(() => {
    const handleRemoteOverwrite = (event) => {
      const { noteId, note: incoming } = event?.detail || {};
      const savedRef = lastSavedRef.current;
      if (!incoming || !noteId || String(noteId) !== String(savedRef.noteId)) return;

      const incomingContent = incoming.content || '';
      const incomingTitle = incoming.title || t('common:unnamed');
      const ourContent = contentRef.current;
      const ourTitle = titleRef.current;

      // Başka biri bizim kaydettiğimizden farklı içerik yazdı (üzerimize yazdı)
      const theyOverwrote = incomingContent !== savedRef.content || incomingTitle !== savedRef.title;
      // Bizim yerel içeriğimiz gelen içerikten farklı (korunması gereken değişikliğimiz var)
      const weHaveDifferent = ourContent !== incomingContent || ourTitle !== incomingTitle;

      if (theyOverwrote && weHaveDifferent) {
        // Offline: never re-save / overwrite-war while read-only
        if (!isServerOnlineRef.current) return;
        // Sonsuz döngü koruması: aynı not için zaten bir kez retry ettik
        if (retriedOverwriteRef.current === noteId) return;
        retriedOverwriteRef.current = noteId;

        // lastSavedRef'i incoming'e sıfırla → auto-save "hasChanges = true" görecek
        lastSavedRef.current = { ...savedRef, content: incomingContent, title: incomingTitle };
        setOverwriteCounter((c) => c + 1); // auto-save useEffect'i zorla tetikle
      }
    };
    window.addEventListener('notesplus-note-updated', handleRemoteOverwrite);
    return () => window.removeEventListener('notesplus-note-updated', handleRemoteOverwrite);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Bağlantı geri gelince ────────────────────────────────────────────────────
  // Kişisel offline kuyruğun sunucuya yazılması NotesContext.flushOfflineQueue içinde.
  // Legacy notesplus_pending_queue_* verisi korunur, otomatik uygulanmaz.
  useEffect(() => {
    prevOnlineRef.current = isServerOnline;
  }, [isServerOnline]);

  // ─── Manuel kayıt (Ctrl+S / Kaydet butonu) ───────────────────────────────────
  const handleSaveNote = useCallback(
    (html) => {
      if (!selectedNote) return;
      // Çevrimdışı ortak notta manuel kayıt da yok
      if (!isServerOnline && (selectedNote.isCollaborative || selectedNote.myRole === 'editor')) return;
      const nextContent = typeof html === 'string' ? html : content;
      return updateNote(
        {
          ...selectedNote,
          title: resolveSaveTitle(),
          content: nextContent,
        },
        { showToast: false }
      );
    },
    [content, selectedNote, resolveSaveTitle, updateNote, isServerOnline]
  );

  // ─── Taslak işlemleri (Faz 1) ────────────────────────────────────────────────
  const handleRestoreDraft = useCallback(() => {
    if (!draft) return;
    setTitle(draft.title || '');
    setContent(draft.content || '');
    setDraft(null);
    setSaveStatus('draft');
  }, [draft]);

  const handleDismissDraft = useCallback(() => {
    if (!selectedNote?.id || !user?.id) return;
    try {
      window.localStorage.removeItem(`notesplus_draft_${user.id}_${selectedNote.id}`);
    } catch { /* ignore */ }
    setDraft(null);
  }, [selectedNote?.id, user?.id]);

  const handleContentChange = useCallback((html) => {
    setContent(html);
    retriedOverwriteRef.current = null; // kullanıcı yazdı → overwrite retry sıfırla
  }, []);

  // Kullanıcı yazdığında delta'yı socket üzerinden diğer editörlere yayınla
  const handleDelta = useCallback((delta) => {
    if (!isServerOnline) return;
    const noteId = selectedNoteRef.current?.id;
    if (!noteId) return;
    socketManager.getSocket()?.emit('note:delta', { noteId, delta });
  }, [isServerOnline]);

  // ─── Render ───────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#3d8ef0] animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {selectedNote ? (
        (isCollaborativeReadOnly || isFrozenCollaborator || isOfflineSharedReadOnly) ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="h-full flex flex-col overflow-auto bg-white dark:bg-[var(--color-bg-base)]"
          >
            {/* Uyarı bandı */}
            {isOfflineSharedReadOnly ? (
              /* İnce, tek satır, midnight tema — genel çevrimdışı banner'ı Header göstergesine taşındı */
              <div className="flex items-center gap-2 px-5 py-2 bg-[#161f31] border-b border-white/[0.07] shrink-0">
                <WifiOff className="w-3.5 h-3.5 text-red-400 shrink-0" />
                <span className="text-[12px] leading-snug text-[#c8d8eb] truncate">
                  {t('readOnly.offlineShared')}
                </span>
              </div>
            ) : isFrozenCollaborator ? (
              <div className="flex items-center gap-2 px-5 py-3 bg-blue-900/20 border-b border-blue-800/40 shrink-0">
                <Lock className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="text-sm text-blue-300">
                  {t('readOnly.frozen')}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-5 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 shrink-0">
                <Crown className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  {t('readOnly.plusRequired')}
                </span>
              </div>
            )}
            {/* Not başlığı */}
            <h1 className="text-2xl font-bold px-5 pt-6 pb-2 text-slate-900 dark:text-white">
              {selectedNote.title || t('common:unnamed')}
            </h1>
            {/* Not içeriği */}
            <div
              className="flex-1 px-5 pb-6 prose prose-slate dark:prose-invert max-w-none overflow-auto"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedNote.content || `<p>${t('emptyContent')}</p>`) }}
            />
          </motion.div>
        ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="h-full"
        >
          <RichNoteEditor
            key={selectedNote.id}
            note={selectedNote}
            title={title}
            content={content}
            onTitleChange={setTitle}
            onContentChange={handleContentChange}
            saveNote={handleSaveNote}
            onBack={() => selectNote(null)}
            saveStatus={saveStatus}
            draft={draft}
            onRestoreDraft={handleRestoreDraft}
            onDismissDraft={handleDismissDraft}
            onDelta={isServerOnline ? handleDelta : undefined}
            collaborationRef={collaborationRef}
          />
        </motion.div>
        )
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="flex-1 flex items-center justify-center"
        >
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-[var(--color-surface-hover)] flex items-center justify-center mb-6 mx-auto border border-white/10">
              <svg className="w-10 h-10 text-[var(--color-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-[var(--color-text-muted)]">{t('selectNote.title')}</h2>
            <p className="text-sm text-[var(--color-text-muted)]">{t('selectNote.description')}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default NotesPage;
